/*!
 * Fogueta — integración del tema Shopify con el checkout externo (frente 02).
 * Contrato API 1.1.0: POST {apiOrigin}/api/v1/checkout-sessions.
 *
 * Un único handler para: botón del carrito lateral, /cart, notificación de carrito,
 * "Comprar ahora" de producto y formularios/enlaces convencionales a /checkout.
 * Solo se carga cuando el ajuste del tema "Checkout Fogueta > Modo" no está en "Apagado".
 *
 * Reglas:
 * - El navegador NUNCA envía precios, totales, descuentos ni URLs de retorno. Solo IDs de
 *   variante (strings) y cantidades. El backend valida catálogo y calcula todo.
 * - buy_now usa SOLO la variante/cantidad del formulario de producto, sin tocar el carrito.
 * - Una Idempotency-Key por intención; reintentos y reentrada reusan la MISMA intención.
 * - No crea cookies de Meta ni dispara eventos de Pixel. Sin secretos en el tema.
 */
(function (global) {
  "use strict";

  var VERSION = "1.0.0";
  var WHATSAPP_NUMBER = "5493412837708";
  var MAX_LINES = 50;
  var MAX_QTY = 99;
  var INTENT_TTL_MS = 60 * 60 * 1000;
  var TOUCH_TTL_MS = 7 * 24 * 60 * 60 * 1000;
  var REQUEST_TIMEOUT_MS = 8000;
  var RETRY_DELAYS_MS = [800, 2500];
  var MAX_RETRY_AFTER_MS = 5000;
  var UTM_KEYS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
  ];
  var ATTRIBUTION_KEYS = [
    "fbclid",
    "fbc",
    "fbp",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "landing_url",
    "referrer",
    "consent",
  ];

  /* ------------------------------------------------------------------ */
  /* Núcleo puro (sin DOM). Probado en tests/shopify-theme.test.ts.      */
  /* ------------------------------------------------------------------ */

  function clip(value, max) {
    if (value === null || value === undefined) return null;
    var s = String(value).trim();
    if (!s || s.length > max) return null; // nunca truncar identificadores
    return s;
  }

  function parseShopifyId(value) {
    if (typeof value === "string" && /^[0-9]{1,30}$/.test(value)) return value;
    if (
      typeof value === "number" &&
      Number.isSafeInteger(value) &&
      value > 0
    )
      return String(value);
    return null;
  }

  /** JSON.parse que conserva IDs Shopify como texto exacto cuando el navegador lo permite. */
  function parseCartJson(text) {
    return JSON.parse(text, function (key, value, context) {
      if (
        (key === "variant_id" || key === "product_id" || key === "id") &&
        typeof value === "number" &&
        context &&
        typeof context.source === "string" &&
        /^[0-9]{1,30}$/.test(context.source)
      )
        return context.source;
      return value;
    });
  }

  function normalizeQuantity(raw) {
    if (raw === null || raw === undefined || raw === "") return 1;
    var s = String(raw).trim();
    if (!/^[0-9]{1,4}$/.test(s)) return null;
    var n = parseInt(s, 10);
    return n >= 1 ? n : null;
  }

  /** Agrupa líneas del carrito Shopify por variante (el backend rechaza variantes repetidas). */
  function itemsFromCart(cart) {
    var lines = (cart && Array.isArray(cart.items) && cart.items) || [];
    if (!lines.length) return { error: "EMPTY_CART" };
    var order = [];
    var byId = {};
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      var id = parseShopifyId(line.variant_id);
      var qty = line.quantity;
      if (!id || typeof qty !== "number" || !Number.isInteger(qty) || qty < 1)
        return { error: "INVALID_ITEM" };
      if (!byId[id]) {
        byId[id] = {
          id: id,
          quantity: 0,
          title: clip(line.product_title, 120) || "Producto",
          variant:
            line.variant_title && !/^default title$/i.test(line.variant_title)
              ? clip(line.variant_title, 120)
              : null,
        };
        order.push(id);
      }
      byId[id].quantity += qty;
    }
    if (order.length > MAX_LINES)
      return { error: "TOO_MANY_LINES", display: displayOf(order, byId) };
    for (var j = 0; j < order.length; j++)
      if (byId[order[j]].quantity > MAX_QTY)
        return {
          error: "QUANTITY_LIMIT",
          title: byId[order[j]].title,
          display: displayOf(order, byId),
        };
    return {
      items: order.map(function (id) {
        return { shopify_variant_id: id, quantity: byId[id].quantity };
      }),
      display: displayOf(order, byId),
    };
  }

  function displayOf(order, byId) {
    return order.map(function (id) {
      return {
        title: byId[id].title,
        variant: byId[id].variant,
        quantity: byId[id].quantity,
      };
    });
  }

  function isHttpUrl(u) {
    return u && (u.protocol === "https:" || u.protocol === "http:");
  }

  /** Landing sin fbclid/gclid/otros parámetros: solo origen, ruta y UTMs. */
  function sanitizeLanding(href) {
    try {
      var u = new URL(href);
      if (!isHttpUrl(u)) return null;
      var out = new URL(u.origin + u.pathname);
      UTM_KEYS.forEach(function (k) {
        var v = clip(u.searchParams.get(k), 255);
        if (v) out.searchParams.set(k, v);
      });
      var s = out.href;
      return s.length <= 2048 ? s : null;
    } catch (_e) {
      return null;
    }
  }

  /** Referrer externo reducido a su origen; mismo sitio o checkout => null. */
  function sanitizeReferrer(referrer, ownOrigins) {
    if (!referrer) return null;
    try {
      var u = new URL(referrer);
      if (!isHttpUrl(u)) return null;
      if ((ownOrigins || []).indexOf(u.origin) !== -1) return null;
      return u.origin + "/";
    } catch (_e) {
      return null;
    }
  }

  /** Toque de campaña presente en la URL de aterrizaje, o null. */
  function extractTouch(href, referrer, ownOrigins, now) {
    var u;
    try {
      u = new URL(href);
    } catch (_e) {
      return null;
    }
    var touch = { fbclid: clip(u.searchParams.get("fbclid"), 255) };
    var any = !!touch.fbclid;
    UTM_KEYS.forEach(function (k) {
      touch[k] = clip(u.searchParams.get(k), 255);
      if (touch[k]) any = true;
    });
    if (!any) return null;
    touch.landing_url = sanitizeLanding(href);
    touch.referrer = sanitizeReferrer(referrer, ownOrigins);
    touch.captured_at = now;
    return touch;
  }

  function firstVisit(href, referrer, ownOrigins, now) {
    var t = { fbclid: null };
    UTM_KEYS.forEach(function (k) {
      t[k] = null;
    });
    t.landing_url = sanitizeLanding(href);
    t.referrer = sanitizeReferrer(referrer, ownOrigins);
    t.captured_at = now;
    return t;
  }

  /**
   * Atribución del contrato (11 claves, null si falta). Identificadores de marketing solo
   * si el consentimiento vigente lo permite. Nunca fabrica fbc/fbp a partir de fbclid.
   */
  function buildAttribution(o) {
    var touch = o.touch || {};
    var consent = o.consent || "unknown";
    var marketingOk =
      consent === "granted" || (consent === "unknown" && !o.requireExplicit);
    var analyticsOk = o.analyticsAllowed !== false;
    var a = {};
    ATTRIBUTION_KEYS.forEach(function (k) {
      a[k] = null;
    });
    if (marketingOk) {
      a.fbclid = clip(touch.fbclid, 255);
      a.fbc = clip(o.cookies && o.cookies._fbc, 255);
      a.fbp = clip(o.cookies && o.cookies._fbp, 255);
    }
    if (analyticsOk) {
      UTM_KEYS.forEach(function (k) {
        a[k] = clip(touch[k], 255);
      });
      a.landing_url = touch.landing_url ? sanitizeLanding(touch.landing_url) : null;
      a.referrer =
        touch.referrer && /^https?:\/\/[^/]+\/$/.test(touch.referrer)
          ? touch.referrer
          : null;
    }
    a.consent = consent === "granted" || consent === "denied" ? consent : "unknown";
    return a;
  }

  function intentSignature(source, items) {
    var sorted = items
      .map(function (i) {
        return i.shopify_variant_id + "x" + i.quantity;
      })
      .sort();
    return source + "|" + sorted.join(",");
  }

  function base64url(bytes) {
    var bin = "";
    for (var i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  }

  function newIdempotencyKey(cryptoObj) {
    var bytes = new Uint8Array(32);
    cryptoObj.getRandomValues(bytes);
    return base64url(bytes); // 43 caracteres [A-Za-z0-9_-]
  }

  function validateApiOrigin(value) {
    try {
      var u = new URL(value);
      if (u.origin !== value) return null; // origen exacto, sin ruta/query/barra final
      if (u.username || u.password) return null;
      if (u.protocol === "https:") return u.origin;
      if (
        u.protocol === "http:" &&
        (u.hostname === "127.0.0.1" || u.hostname === "localhost")
      )
        return u.origin; // solo desarrollo local
      return null;
    } catch (_e) {
      return null;
    }
  }

  /** El checkout_url debe ser del origen configurado, raíz y con ticket. Nada arbitrario. */
  function validateCheckoutUrl(value, apiOrigin) {
    try {
      var u = new URL(value);
      if (u.origin !== apiOrigin || u.pathname !== "/") return null;
      if (!/^[A-Za-z0-9_-]{43}$/.test(u.searchParams.get("ticket") || ""))
        return null;
      return u.href;
    } catch (_e) {
      return null;
    }
  }

  /** Clasifica la respuesta HTTP de createSession. */
  function classify(status, body) {
    var code = (body && body.error && body.error.code) || null;
    if (status === 201 && body && typeof body.checkout_url === "string")
      return { type: "success" };
    if (status === 0) return { type: "retry", code: "NETWORK" };
    if (code === "PRODUCT_UNAVAILABLE" || code === "EMPTY_CART")
      return { type: "unavailable", code: code };
    if (code === "IDEMPOTENCY_CONFLICT")
      return { type: "idempotency_conflict", code: code };
    if (status === 429 || code === "RATE_LIMITED")
      return { type: "rate_limited", code: "RATE_LIMITED" };
    if (status === 400 || status === 413 || status === 422 || code === "VALIDATION_ERROR")
      return { type: "validation", code: code || "VALIDATION_ERROR" };
    if (status === 408 || status === 500 || status === 502 || status === 503 || status === 504)
      return { type: "retry", code: code || "HTTP_" + status };
    return { type: "technical", code: code || "HTTP_" + status };
  }

  function itemLine(d) {
    return (
      "• " +
      d.title +
      (d.variant ? " (" + d.variant + ")" : "") +
      " × " +
      d.quantity
    );
  }

  /**
   * Enlace wa.me al número comercial. Sin precios: nunca garantiza importes no validados.
   * kind "technical": intención de compra con cotización pendiente.
   * kind "unavailable": SOLO consulta de disponibilidad (no compra de un producto bloqueado).
   */
  function whatsappUrl(kind, display) {
    var lines = (display || []).slice(0, 20).map(itemLine);
    var text;
    if (kind === "unavailable")
      text =
        "Hola, quería consultar por estos productos que la tienda online no me permitió comprar:\n\n" +
        (lines.join("\n") || "• (sin detalle)") +
        "\n\n¿Me pueden informar su disponibilidad?";
    else
      text =
        "Hola, quiero comprar en la tienda online de Fogueta pero el checkout no respondió.\n\n" +
        (lines.length ? "Productos:\n" + lines.join("\n") + "\n\n" : "") +
        "Entiendo que el precio final, los descuentos y el envío quedan pendientes de confirmación por este medio.";
    if (text.length > 1800) text = text.slice(0, 1790) + "…";
    return "https://wa.me/" + WHATSAPP_NUMBER + "?text=" + encodeURIComponent(text);
  }

  /** Quita el prefijo de idioma (/en/) usando Shopify.routes.root. */
  function stripLocale(pathname, root) {
    var r = root || "/";
    if (r !== "/" && pathname.indexOf(r) === 0) return "/" + pathname.slice(r.length);
    if (r !== "/" && pathname + "/" === r) return "/";
    return pathname;
  }

  /** ¿Es una ruta de checkout convencional de Shopify en este mismo dominio? */
  function isCheckoutPath(path) {
    return path === "/checkout" || path === "/checkout/" || path.indexOf("/checkouts/") === 0;
  }

  var Core = {
    VERSION: VERSION,
    WHATSAPP_NUMBER: WHATSAPP_NUMBER,
    MAX_LINES: MAX_LINES,
    MAX_QTY: MAX_QTY,
    clip: clip,
    parseShopifyId: parseShopifyId,
    parseCartJson: parseCartJson,
    normalizeQuantity: normalizeQuantity,
    itemsFromCart: itemsFromCart,
    sanitizeLanding: sanitizeLanding,
    sanitizeReferrer: sanitizeReferrer,
    extractTouch: extractTouch,
    firstVisit: firstVisit,
    buildAttribution: buildAttribution,
    intentSignature: intentSignature,
    newIdempotencyKey: newIdempotencyKey,
    validateApiOrigin: validateApiOrigin,
    validateCheckoutUrl: validateCheckoutUrl,
    classify: classify,
    whatsappUrl: whatsappUrl,
    stripLocale: stripLocale,
    isCheckoutPath: isCheckoutPath,
  };
  global.FoguetaCheckoutCore = Core;

  var doc = global.document;
  if (!doc || !doc.documentElement) return; // entorno de pruebas sin DOM
  if (global.__foguetaCheckoutBooted) return; // protección contra doble carga
  global.__foguetaCheckoutBooted = true;

  /* ------------------------------------------------------------------ */
  /* Navegador                                                          */
  /* ------------------------------------------------------------------ */

  var html = doc.documentElement;
  var config = readConfig();
  var state = {
    active: false,
    busy: null, // {source, context}
    wired: typeof WeakSet === "function" ? new WeakSet() : null,
    memory: {},
    scanQueued: false,
  };

  function readConfig() {
    var el = doc.getElementById("fogueta-checkout-config");
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || "{}");
    } catch (_e) {
      return null;
    }
  }

  function storage(kind) {
    try {
      var s = global[kind];
      var probe = "__fcheckout_probe";
      s.setItem(probe, "1");
      s.removeItem(probe);
      return s;
    } catch (_e) {
      return null;
    }
  }

  function readJson(kind, key) {
    var s = storage(kind);
    try {
      var raw = s ? s.getItem(key) : state.memory[kind + key];
      return raw ? JSON.parse(raw) : null;
    } catch (_e) {
      return null;
    }
  }

  function writeJson(kind, key, value) {
    var raw = value === null ? null : JSON.stringify(value);
    var s = storage(kind);
    try {
      if (s) {
        if (raw === null) s.removeItem(key);
        else s.setItem(key, raw);
        return;
      }
    } catch (_e) {
      /* almacenamiento lleno o bloqueado: usar memoria */
    }
    if (raw === null) delete state.memory[kind + key];
    else state.memory[kind + key] = raw;
  }

  function isTestFlagOn() {
    var params;
    try {
      params = new URL(global.location.href).searchParams;
    } catch (_e) {
      return false;
    }
    var s = storage("sessionStorage");
    if (params.get("fcheckout_test") === "1") {
      if (s) s.setItem("fcheckout_test", "1");
      return true;
    }
    if (params.get("fcheckout_test") === "0") {
      if (s) s.removeItem("fcheckout_test");
      return false;
    }
    return !!(s && s.getItem("fcheckout_test") === "1");
  }

  function computeActive() {
    if (!config) return false;
    config.apiOrigin = validateApiOrigin(String(config.apiOrigin || ""));
    if (!config.apiOrigin) {
      warn("Origen del checkout inválido; se mantiene el checkout del tema.");
      report("CONFIG_INVALID_ORIGIN", "boot");
      return false;
    }
    if (config.mode === "on") return true;
    if (config.mode === "test") return isTestFlagOn();
    return false;
  }

  function warn(msg) {
    try {
      if (global.console) global.console.warn("[Fogueta checkout] " + msg);
    } catch (_e) {
      /* noop */
    }
  }

  /** Registro best effort: no depende del VPS. Sin PII ni importes. */
  function report(code, stage, extra) {
    var entry = {
      code: String(code || "UNKNOWN").slice(0, 60),
      stage: stage,
      source: (extra && extra.source) || null,
      items: (extra && extra.items) || null,
      at: new Date().toISOString(),
      v: VERSION,
    };
    try {
      var log = readJson("sessionStorage", "fcheckout_errors") || [];
      log.push(entry);
      writeJson("sessionStorage", "fcheckout_errors", log.slice(-10));
    } catch (_e) {
      /* noop */
    }
    try {
      if (Array.isArray(global.dataLayer))
        global.dataLayer.push({
          event: "fogueta_checkout_error",
          fogueta_error_code: entry.code,
          fogueta_error_stage: stage,
          fogueta_source: entry.source,
        });
    } catch (_e) {
      /* noop */
    }
    try {
      var analytics = global.Shopify && global.Shopify.analytics;
      if (analytics && typeof analytics.publish === "function")
        analytics.publish("fogueta_checkout_error", entry);
    } catch (_e) {
      /* noop */
    }
  }

  /* ----------------------------- Consentimiento ----------------------------- */

  function privacyApi() {
    var cp = global.Shopify && global.Shopify.customerPrivacy;
    return cp && typeof cp.marketingAllowed === "function" ? cp : null;
  }

  function currentConsent() {
    var cp = privacyApi();
    if (!cp) return "unknown";
    try {
      if (typeof cp.currentVisitorConsent === "function") {
        var c = cp.currentVisitorConsent() || {};
        if (c.marketing === "yes") return "granted";
        if (c.marketing === "no") return "denied";
      }
      return cp.marketingAllowed() ? "granted" : "denied";
    } catch (_e) {
      return "unknown";
    }
  }

  function analyticsAllowed() {
    var cp = privacyApi();
    if (!cp || typeof cp.analyticsProcessingAllowed !== "function") return true;
    try {
      return cp.analyticsProcessingAllowed() !== false;
    } catch (_e) {
      return true;
    }
  }

  function loadPrivacyApi(done) {
    if (privacyApi()) return done();
    var finished = false;
    var finish = function () {
      if (finished) return;
      finished = true;
      done();
    };
    try {
      var sh = global.Shopify;
      if (sh && typeof sh.loadFeatures === "function")
        sh.loadFeatures(
          [{ name: "consent-tracking-api", version: "0.1" }],
          function () {
            finish();
          },
        );
    } catch (_e) {
      /* noop */
    }
    global.setTimeout(finish, 1500);
  }

  /* ------------------------------- Atribución ------------------------------- */

  var TOUCH_KEY = "fcheckout_touch_v1";

  function ownOrigins() {
    var list = [global.location.origin];
    if (config && config.apiOrigin) list.push(config.apiOrigin);
    return list;
  }

  function marketingStorable() {
    var consent = currentConsent();
    return consent === "granted" || (consent === "unknown" && !config.requireExplicitConsent);
  }

  function captureTouch() {
    var now = Date.now();
    var stored = readJson("localStorage", TOUCH_KEY);
    if (stored && now - (stored.captured_at || 0) > TOUCH_TTL_MS) stored = null;
    var touch = extractTouch(global.location.href, doc.referrer, ownOrigins(), now);
    if (!touch && !stored) touch = firstVisit(global.location.href, doc.referrer, ownOrigins(), now);
    if (!touch) return;
    if (!marketingStorable()) touch.fbclid = null;
    writeJson("localStorage", TOUCH_KEY, touch);
  }

  function purgeMarketingIfDenied() {
    if (marketingStorable()) return;
    var stored = readJson("localStorage", TOUCH_KEY);
    if (stored && stored.fbclid) {
      stored.fbclid = null;
      writeJson("localStorage", TOUCH_KEY, stored);
    }
  }

  function readCookie(name) {
    var parts = ("; " + (doc.cookie || "")).split("; " + name + "=");
    if (parts.length < 2) return null;
    try {
      return decodeURIComponent(parts.pop().split(";")[0]);
    } catch (_e) {
      return null;
    }
  }

  function currentAttribution() {
    var touch = readJson("localStorage", TOUCH_KEY);
    if (touch && Date.now() - (touch.captured_at || 0) > TOUCH_TTL_MS) touch = null;
    return buildAttribution({
      touch: touch,
      cookies: { _fbc: readCookie("_fbc"), _fbp: readCookie("_fbp") },
      consent: currentConsent(),
      analyticsAllowed: analyticsAllowed(),
      requireExplicit: !!config.requireExplicitConsent,
    });
  }

  /* -------------------------------- Intención -------------------------------- */

  var INTENTS_KEY = "fcheckout_intents_v1";

  function loadIntents() {
    var all = readJson("sessionStorage", INTENTS_KEY) || {};
    var now = Date.now();
    Object.keys(all).forEach(function (sig) {
      if (!all[sig] || now - all[sig].createdAt > INTENT_TTL_MS) delete all[sig];
    });
    return all;
  }

  function getIntent(source, items) {
    var sig = intentSignature(source, items);
    var all = loadIntents();
    if (all[sig]) return all[sig];
    var intent = {
      sig: sig,
      key: newIdempotencyKey(global.crypto),
      body: { source: source, items: items, attribution: currentAttribution() },
      createdAt: Date.now(),
    };
    all[sig] = intent;
    writeJson("sessionStorage", INTENTS_KEY, all);
    return intent;
  }

  function dropIntent(sig) {
    var all = loadIntents();
    delete all[sig];
    writeJson("sessionStorage", INTENTS_KEY, all);
  }

  /* ------------------------------ Red (API) ------------------------------ */

  function sleep(ms) {
    return new Promise(function (r) {
      global.setTimeout(r, ms);
    });
  }

  function postOnce(intent) {
    var controller = typeof AbortController === "function" ? new AbortController() : null;
    var timer = controller
      ? global.setTimeout(function () {
          controller.abort();
        }, REQUEST_TIMEOUT_MS)
      : null;
    return global
      .fetch(config.apiOrigin + "/api/v1/checkout-sessions", {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        cache: "no-store",
        referrerPolicy: "strict-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Idempotency-Key": intent.key,
        },
        body: JSON.stringify(intent.body),
        signal: controller ? controller.signal : undefined,
      })
      .then(function (res) {
        var retryAfter = parseInt(res.headers.get("Retry-After") || "", 10);
        return res
          .json()
          .catch(function () {
            return null;
          })
          .then(function (body) {
            return {
              status: res.status,
              body: body,
              retryAfterMs: Number.isFinite(retryAfter) ? retryAfter * 1000 : null,
            };
          });
      })
      .finally(function () {
        if (timer) global.clearTimeout(timer);
      });
  }

  function createSession(intent, onRetry) {
    var attempt = 0;
    var last = null;
    function next() {
      return postOnce(intent).then(
        function (res) {
          var c = classify(res.status, res.body);
          c.body = res.body;
          var canRetryRate =
            c.type === "rate_limited" &&
            attempt === 0 &&
            (res.retryAfterMs === null || res.retryAfterMs <= MAX_RETRY_AFTER_MS);
          if ((c.type === "retry" || canRetryRate) && attempt < RETRY_DELAYS_MS.length) {
            last = c;
            return again(canRetryRate && res.retryAfterMs ? res.retryAfterMs : null);
          }
          if (c.type === "retry") return { type: "technical", code: c.code, exhausted: true };
          return c;
        },
        function (err) {
          last = {
            type: "retry",
            code: err && err.name === "AbortError" ? "TIMEOUT" : "NETWORK",
          };
          if (attempt < RETRY_DELAYS_MS.length) return again(null);
          return { type: "technical", code: last.code, exhausted: true };
        },
      );
    }
    function again(waitMs) {
      var base = waitMs || RETRY_DELAYS_MS[attempt];
      attempt += 1;
      if (onRetry) onRetry(attempt, last);
      return sleep(base + Math.floor(Math.random() * 300)).then(next);
    }
    return next();
  }

  /* ----------------------------- Lectura de datos ----------------------------- */

  function rootUrl() {
    var r = (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || config.root || "/";
    return r.charAt(r.length - 1) === "/" ? r : r + "/";
  }

  function cartIsUpdating() {
    return !!doc.querySelector(
      ".cart__items--disabled, cart-drawer-items .cart-item__totals .loading__spinner:not(.hidden), cart-items .cart-item__totals .loading__spinner:not(.hidden)",
    );
  }

  function waitCartIdle() {
    var started = Date.now();
    return new Promise(function (resolve) {
      (function poll() {
        if (!cartIsUpdating() || Date.now() - started > 4000) return resolve();
        global.setTimeout(poll, 100);
      })();
    });
  }

  function fetchCart() {
    return waitCartIdle()
      .then(function () {
        return global.fetch(rootUrl() + "cart.js", {
          method: "GET",
          credentials: "same-origin",
          cache: "no-store",
          headers: { Accept: "application/json" },
        });
      })
      .then(function (res) {
        if (!res.ok) throw new Error("CART_HTTP_" + res.status);
        return res.text();
      })
      .then(parseCartJson);
  }

  function sectionOfForm(form) {
    // getAttribute: form.id devolvería el <input name="id"> (DOM clobbering).
    return (form.getAttribute("id") || "").replace(/^product-form-/, "");
  }

  function readProductSelection(formId, root) {
    var form = formId ? doc.getElementById(formId) : null;
    if (!form) return { error: "FORM_NOT_FOUND" };
    var idInput = form.querySelector('[name="id"]');
    var add = form.querySelector('[name="add"]');
    if (!idInput || idInput.disabled || (add && add.disabled))
      return { error: "VARIANT_UNAVAILABLE" };
    var section = sectionOfForm(form);
    var picker = section
      ? doc.querySelector(
          'variant-selects[data-section="' + section + '"], variant-radios[data-section="' + section + '"]',
        )
      : null;
    if (picker && picker.options && !picker.currentVariant) return { error: "VARIANT_UNAVAILABLE" };
    if (picker && picker.currentVariant && String(picker.currentVariant.id) !== String(idInput.value))
      return { error: "VARIANT_PENDING" };
    var id = parseShopifyId(String(idInput.value || ""));
    if (!id) return { error: "VARIANT_UNAVAILABLE" };
    var qty = normalizeQuantity(new FormData(form).get("quantity"));
    if (qty === null) return { error: "QUANTITY_INVALID" };
    if (qty > MAX_QTY) return { error: "QUANTITY_LIMIT" };
    var meta = productMeta(root);
    return {
      items: [{ shopify_variant_id: id, quantity: qty }],
      display: [
        {
          title: meta.title || "Producto",
          variant: (meta.variants && meta.variants[id]) || null,
          quantity: qty,
        },
      ],
    };
  }

  function productMeta(root) {
    var el = root && root.querySelector("script[data-fcheckout-product]");
    if (!el) return {};
    try {
      return JSON.parse(el.textContent || "{}");
    } catch (_e) {
      return {};
    }
  }

  /* --------------------------------- UI --------------------------------- */

  var MESSAGES = {
    EMPTY_CART: "Tu carrito está vacío. Agregá un producto para continuar.",
    INVALID_ITEM: "No pudimos leer tu carrito. Actualizá la página e intentá de nuevo.",
    TOO_MANY_LINES:
      "El carrito tiene demasiados productos distintos (máximo " + MAX_LINES + "). Quitá algunos para continuar.",
    QUANTITY_LIMIT: "Podés comprar hasta " + MAX_QTY + " unidades de cada producto. Ajustá la cantidad para continuar.",
    QUANTITY_INVALID: "Revisá la cantidad elegida.",
    VARIANT_UNAVAILABLE: "Elegí una variante disponible para continuar.",
    VARIANT_PENDING: "Estamos actualizando la variante elegida. Probá de nuevo en un instante.",
    FORM_NOT_FOUND: "No pudimos leer el producto. Actualizá la página e intentá de nuevo.",
    CART_READ: "No pudimos leer tu carrito. Revisá tu conexión e intentá de nuevo.",
    VALIDATION:
      "Revisá los productos y cantidades del carrito (máximo " + MAX_QTY + " unidades por producto).",
    RATE_LIMITED: "Hay muchas solicitudes en este momento. Esperá unos segundos y volvé a intentar.",
    UNAVAILABLE:
      "Uno o más productos no están disponibles para compra online en este momento. Revisá tu carrito o consultanos por WhatsApp.",
    TECHNICAL:
      "No pudimos conectar con el checkout. Podés reintentar o seguir por WhatsApp: el precio final, los descuentos y el envío te los confirmamos por ese medio y no están garantizados hasta entonces.",
    RETRYING: "Conectando con el checkout… (reintento %n)",
  };

  function buttons() {
    return Array.prototype.slice.call(doc.querySelectorAll("[data-fcheckout-button]"));
  }

  function contextKey(source, context) {
    return source + ":" + (context || "global");
  }

  function applyBusy() {
    buttons().forEach(function (btn) {
      var mine =
        state.busy &&
        contextKey(btn.getAttribute("data-fcheckout-button"), btn.getAttribute("data-fcheckout-context")) ===
          contextKey(state.busy.source, state.busy.context);
      btn.classList.toggle("is-loading", !!mine);
      if (mine) btn.setAttribute("aria-busy", "true");
      else btn.removeAttribute("aria-busy");
      syncDisabled(btn);
    });
  }

  function syncDisabled(btn) {
    var disabled = !!state.busy;
    if (!disabled && btn.getAttribute("data-fcheckout-button") === "buy_now") {
      var form = doc.getElementById(btn.getAttribute("data-product-form-id") || "");
      var add = form && form.querySelector('[name="add"]');
      var idInput = form && form.querySelector('[name="id"]');
      disabled = !form || !idInput || idInput.disabled || !!(add && add.disabled);
    }
    if (!disabled && btn.hasAttribute("data-fcheckout-empty")) disabled = true;
    btn.disabled = disabled;
  }

  function statusHost(root) {
    var el = root && root.querySelector("[data-fcheckout-status]");
    if (el) return el;
    var toast = doc.getElementById("fcheckout-toast");
    if (!toast) {
      toast = doc.createElement("div");
      toast.id = "fcheckout-toast";
      toast.className = "fcheckout-toast";
      toast.setAttribute("role", "status");
      toast.setAttribute("aria-live", "polite");
      toast.setAttribute("data-fcheckout-status", "");
      doc.body.appendChild(toast);
    }
    return toast;
  }

  function showStatus(root, status) {
    var host = statusHost(root);
    while (host.firstChild) host.removeChild(host.firstChild);
    if (!status) {
      host.hidden = true;
      host.className = host.id === "fcheckout-toast" ? "fcheckout-toast" : "fcheckout__status";
      return;
    }
    host.hidden = false;
    host.className =
      (host.id === "fcheckout-toast" ? "fcheckout-toast" : "fcheckout__status") +
      " fcheckout__status--" +
      (status.tone || "info");
    var p = doc.createElement("p");
    p.className = "fcheckout__message";
    p.textContent = status.message;
    host.appendChild(p);
    if (status.actions && status.actions.length) {
      var actions = doc.createElement("div");
      actions.className = "fcheckout__actions";
      status.actions.forEach(function (a) {
        var el;
        if (a.href) {
          el = doc.createElement("a");
          el.href = a.href;
          el.target = "_blank";
          el.rel = "noopener noreferrer";
          el.setAttribute("data-fcheckout-whatsapp", a.kind || "");
        } else {
          el = doc.createElement("button");
          el.type = "button";
          el.addEventListener("click", a.onClick);
        }
        el.className = "fcheckout__action" + (a.primary ? " fcheckout__action--primary" : "");
        el.textContent = a.label;
        actions.appendChild(el);
      });
      host.appendChild(actions);
    }
    if (host.id === "fcheckout-toast") {
      var close = doc.createElement("button");
      close.type = "button";
      close.className = "fcheckout-toast__close";
      close.setAttribute("aria-label", "Cerrar");
      close.textContent = "×";
      close.addEventListener("click", function () {
        showStatus(null, null);
      });
      host.appendChild(close);
    }
  }

  /* ------------------------------- Flujo ------------------------------- */

  function start(source, context, root, formId) {
    if (!state.active || state.busy) return; // doble clic / listeners duplicados
    state.busy = { source: source, context: context };
    applyBusy();
    showStatus(root, null);
    var display = [];
    var gather =
      source === "cart"
        ? fetchCart().then(
            function (cart) {
              return itemsFromCart(cart);
            },
            function () {
              return { error: "CART_READ" };
            },
          )
        : new Promise(function (resolve) {
            resolve(readProductSelection(formId, root)); // excepciones => catch, nunca queda en carga
          });
    return gather
      .then(function (sel) {
        display = sel.display || [];
        if (sel.error) {
          report(sel.error, "selection", { source: source });
          return finish(root, { tone: "error", message: MESSAGES[sel.error] || MESSAGES.CART_READ });
        }
        var intent = getIntent(source, sel.items);
        return send(intent, source, root, display, true);
      })
      .catch(function (err) {
        report((err && err.message) || "UNEXPECTED", "flow", { source: source });
        return finish(root, technicalStatus(source, context, root, formId, display));
      });
  }

  function send(intent, source, root, display, allowFreshKey) {
    return createSession(intent, function (n) {
      showStatus(root, { tone: "info", message: MESSAGES.RETRYING.replace("%n", String(n)) });
    }).then(function (result) {
      var items = intent.body.items.length;
      if (result.type === "success") {
        var url = validateCheckoutUrl(result.body.checkout_url, config.apiOrigin);
        if (!url) {
          report("CHECKOUT_URL_REJECTED", "response", { source: source, items: items });
          return finish(root, technicalStatusFor(intent, source, root, display));
        }
        intent.redirectedAt = Date.now();
        var all = loadIntents();
        all[intent.sig] = intent;
        writeJson("sessionStorage", INTENTS_KEY, all);
        showStatus(root, null);
        global.location.assign(url);
        // Se mantiene el estado de carga hasta navegar; si la navegación se cancela, liberar.
        global.setTimeout(function () {
          if (state.busy) {
            state.busy = null;
            applyBusy();
          }
        }, 15000);
        return;
      }
      if (result.type === "idempotency_conflict" && allowFreshKey) {
        dropIntent(intent.sig);
        var fresh = getIntent(source, intent.body.items);
        return send(fresh, source, root, display, false);
      }
      report(result.code, "create_session", { source: source, items: items });
      if (result.type === "unavailable") {
        dropIntent(intent.sig);
        return finish(root, {
          tone: "error",
          message: MESSAGES.UNAVAILABLE,
          actions: [
            {
              label: "Consultar disponibilidad por WhatsApp",
              href: whatsappUrl("unavailable", display),
              kind: "unavailable",
            },
          ],
        });
      }
      if (result.type === "validation") {
        dropIntent(intent.sig);
        return finish(root, { tone: "error", message: MESSAGES.VALIDATION });
      }
      if (result.type === "rate_limited")
        return finish(root, {
          tone: "error",
          message: MESSAGES.RATE_LIMITED,
          actions: [retryAction(intent, source, root, display)],
        });
      return finish(root, technicalStatusFor(intent, source, root, display));
    });
  }

  function retryAction(intent, source, root, display) {
    return {
      label: "Reintentar",
      primary: true,
      onClick: function () {
        if (state.busy) return;
        state.busy = { source: source, context: state.lastContext };
        applyBusy();
        showStatus(root, null);
        send(intent, source, root, display, true).catch(function () {
          finish(root, technicalStatusFor(intent, source, root, display));
        });
      },
    };
  }

  function technicalStatusFor(intent, source, root, display) {
    return {
      tone: "error",
      message: MESSAGES.TECHNICAL,
      actions: [
        retryAction(intent, source, root, display),
        { label: "Continuar por WhatsApp", href: whatsappUrl("technical", display), kind: "technical" },
      ],
    };
  }

  function technicalStatus(source, context, root, formId, display) {
    return {
      tone: "error",
      message: MESSAGES.TECHNICAL,
      actions: [
        {
          label: "Reintentar",
          primary: true,
          onClick: function () {
            start(source, context, root, formId);
          },
        },
        { label: "Continuar por WhatsApp", href: whatsappUrl("technical", display), kind: "technical" },
      ],
    };
  }

  function finish(root, status) {
    state.lastContext = state.busy && state.busy.context;
    state.busy = null;
    applyBusy();
    showStatus(root, status);
  }

  /* --------------------------- Eventos (una vez) --------------------------- */

  function onClick(e) {
    if (!state.active || !e.target || !e.target.closest) return;
    var btn = e.target.closest("[data-fcheckout-button]");
    if (btn) {
      e.preventDefault();
      e.stopPropagation();
      if (btn.disabled) return;
      var source = btn.getAttribute("data-fcheckout-button") === "buy_now" ? "buy_now" : "cart";
      start(
        source,
        btn.getAttribute("data-fcheckout-context") || source,
        btn.closest("[data-fcheckout-root]"),
        btn.getAttribute("data-product-form-id"),
      );
      return;
    }
    var a = e.target.closest("a[href]");
    if (!a || a.hasAttribute("data-fcheckout-ignore")) return;
    var u;
    try {
      u = new URL(a.getAttribute("href"), global.location.href);
    } catch (_e) {
      return;
    }
    if (u.origin !== global.location.origin) return; // nunca enlaces externos
    if (!isCheckoutPath(stripLocale(u.pathname, rootUrl()))) return;
    e.preventDefault();
    e.stopPropagation();
    start("cart", "link", null, null);
  }

  function onSubmit(e) {
    if (!state.active) return;
    var form = e.target;
    if (!form || form.tagName !== "FORM") return;
    var u;
    try {
      u = new URL(form.getAttribute("action") || global.location.href, global.location.href);
    } catch (_e) {
      return;
    }
    if (u.origin !== global.location.origin) return;
    var path = stripLocale(u.pathname, rootUrl());
    var submitter = e.submitter || null;
    var checkoutSubmit =
      (submitter && submitter.getAttribute("name") === "checkout") ||
      !!form.querySelector('input[type="hidden"][name="checkout"]');
    if (isCheckoutPath(path)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (form.querySelector('[name="id"]')) {
        var fid = form.getAttribute("id");
        if (!fid) {
          fid = "fcheckout-form-" + Math.random().toString(36).slice(2);
          form.setAttribute("id", fid);
        }
        start("buy_now", "form", null, fid);
      } else start("cart", "form", null, null);
      return;
    }
    if ((path === "/cart" || path === "/cart/") && checkoutSubmit) {
      e.preventDefault();
      e.stopImmediatePropagation();
      start("cart", "form", null, null);
    }
    // /cart/add, /cart/change, /cart/update: intactos (Add to cart no se rompe).
  }

  function onChange(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var form = t.form || t.closest("form");
    var key = form && form.getAttribute("id");
    if (!key && t.closest("variant-selects, variant-radios")) key = "picker";
    if (!key && !/quantity/.test(t.name || "")) return;
    // Cambió variante o cantidad: limpiar mensajes viejos del producto y resincronizar.
    buttons().forEach(function (btn) {
      if (btn.getAttribute("data-fcheckout-button") === "buy_now" && !state.busy) {
        var root = btn.closest("[data-fcheckout-root]");
        var status = root && root.querySelector("[data-fcheckout-status]");
        if (status && !status.hidden) showStatus(root, null);
      }
    });
    scheduleScan();
  }

  function wire(btn) {
    if (state.wired) {
      if (state.wired.has(btn)) return;
      state.wired.add(btn);
    } else if (btn.__fcheckoutWired) return;
    else btn.__fcheckoutWired = true;
    if (btn.getAttribute("data-fcheckout-button") !== "buy_now") return;
    var form = doc.getElementById(btn.getAttribute("data-product-form-id") || "");
    if (!form || typeof MutationObserver !== "function") return;
    var targets = [form.querySelector('[name="add"]'), form.querySelector('[name="id"]')].filter(Boolean);
    var mo = new MutationObserver(function () {
      syncDisabled(btn);
    });
    targets.forEach(function (t) {
      mo.observe(t, { attributes: true, attributeFilter: ["disabled"] });
    });
  }

  function scan() {
    state.scanQueued = false;
    buttons().forEach(function (btn) {
      wire(btn);
    });
    applyBusy();
  }

  function scheduleScan() {
    if (state.scanQueued) return;
    state.scanQueued = true;
    (global.requestAnimationFrame || global.setTimeout)(scan);
  }

  function boot() {
    state.active = computeActive();
    if (!state.active) {
      html.classList.remove("fcheckout-active");
      return;
    }
    html.classList.add("fcheckout-active");
    doc.addEventListener("click", onClick, true);
    doc.addEventListener("submit", onSubmit, true);
    doc.addEventListener("change", onChange, true);
    doc.addEventListener("visitorConsentCollected", purgeMarketingIfDenied);
    global.addEventListener("pageshow", function (e) {
      if (e.persisted) {
        state.busy = null;
        applyBusy();
      }
    });
    if (typeof MutationObserver === "function")
      new MutationObserver(scheduleScan).observe(doc.body || html, { childList: true, subtree: true });
    scan();
    // Captura inmediata con el consentimiento conocido; se reevalúa cuando carga la API de
    // privacidad de Shopify (puede agregar fbclid si ahora se permite, o purgarlo si se negó).
    var capture = function () {
      try {
        captureTouch();
        purgeMarketingIfDenied();
      } catch (_e) {
        /* nunca bloquear la compra por atribución */
      }
    };
    capture();
    loadPrivacyApi(capture);
    global.FoguetaCheckout = {
      version: VERSION,
      isActive: function () {
        return state.active;
      },
    };
  }

  if (doc.readyState === "loading") doc.addEventListener("DOMContentLoaded", boot);
  else boot();
})(typeof window !== "undefined" ? window : globalThis);
